import { useEffect, useRef } from 'react';

type Params = {
  enabled: boolean;
  deps: unknown[]; // 监控的数据（例如 profileData）
  isDirty: boolean;
  onSave: () => Promise<void> | void;
  debounceMs?: number; // 默认 1200ms
  minIntervalMs?: number; // 默认 2000ms
};

export function useDebouncedSave(params: Params) {
  const {
    enabled,
    deps,
    isDirty,
    onSave,
    debounceMs = 1200,
    minIntervalMs = 2000,
  } = params;

  const timerRef = useRef<number | null>(null);
  const lastSaveAtRef = useRef<number>(0);
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (!enabled || !isDirty) return;

    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    timerRef.current = window.setTimeout(async () => {
      const now = Date.now();
      const elapsed = now - lastSaveAtRef.current;
      if (elapsed < minIntervalMs) {
        const delay = minIntervalMs - elapsed;
        if (timerRef.current) window.clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(async () => {
          const id = ++reqIdRef.current;
          await Promise.resolve(onSave());
          if (id === reqIdRef.current) lastSaveAtRef.current = Date.now();
        }, delay);
        return;
      }

      const id = ++reqIdRef.current;
      await Promise.resolve(onSave());
      if (id === reqIdRef.current) lastSaveAtRef.current = Date.now();
    }, debounceMs);

    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isDirty, debounceMs, minIntervalMs, ...deps]);
}
